import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./i18n";
import Layout from "./components/Layout";
import PullToRefresh from "./components/PullToRefresh";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import LegalPage from "./pages/LegalPage";
import Signup from "./pages/Signup";
import CredentialGate from "./components/CredentialGate";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import Ranges from "./pages/Ranges";
import Stock from "./pages/Stock";
import Reminders from "./pages/Reminders";
import Purchases from "./pages/Purchases";
import PreOrders from "./pages/PreOrders";
import Offers from "./pages/Offers";
import Settings from "./pages/Settings";
import Devices from "./pages/Devices";
import Expenses from "./pages/Expenses";
import Deliveries from "./pages/Deliveries";
import Materials from "./pages/Materials";
import Recipes from "./pages/Recipes";
import Batches from "./pages/Batches";
import ShopProfile from "./pages/ShopProfile";
import Packs from "./pages/Packs";
import Revenue from "./pages/Revenue";
import Credit from "./pages/Credit";
import Staff from "./pages/Staff";
import Journal from "./pages/Journal";
import Suppliers from "./pages/Suppliers";
import CashDrawer from "./pages/CashDrawer";
import Savings from "./pages/Savings";
import Modules from "./pages/Modules";
import Subscription from "./pages/Subscription";
import Referrals from "./pages/Referrals";
import ErrorBoundary from "./components/ErrorBoundary";
import StagingBanner from "./components/StagingBanner";
import PosRegister from "./pos/PosRegister";
import ActivateDevice from "./pages/ActivateDevice";
import SuspendedScreen from "./pages/SuspendedScreen";
import { deviceToken, shopSession } from "./services/api";
import { MembershipProvider } from "./context/MembershipContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import RequirePermission from "./components/RequirePermission";

/** Every admin screen, and which navigation entry's permission guards it. */
const ADMIN_SCREENS = [
  { path: undefined, screen: "dashboard", Page: Dashboard },
  { path: "products", screen: "products", Page: Products },
  { path: "products/new", screen: "products", Page: Products },
  { path: "ranges", screen: "ranges", Page: Ranges },
  { path: "stock", screen: "stock", Page: Stock },
  { path: "reminders", screen: "reminders", Page: Reminders },
  { path: "purchases", screen: "purchases", Page: Purchases },
  { path: "preorders", screen: "preorders", Page: PreOrders },
  { path: "offers", screen: "offers", Page: Offers },
  { path: "settings", screen: "settings", Page: Settings },
  { path: "expenses", screen: "expenses", Page: Expenses },
  { path: "deliveries", screen: "deliveries", Page: Deliveries },
  { path: "materials", screen: "materials", Page: Materials },
  { path: "recipes", screen: "recipes", Page: Recipes },
  { path: "batches", screen: "batches", Page: Batches },
  { path: "shop", screen: "shop", Page: ShopProfile },
  { path: "packs", screen: "packs", Page: Packs },
  { path: "revenue", screen: "revenue", Page: Revenue },
  { path: "credit", screen: "credit", Page: Credit },
  { path: "devices", screen: "devices", Page: Devices },
  { path: "staff", screen: "staff", Page: Staff },
  { path: "journal", screen: "journal", Page: Journal },
  { path: "suppliers", screen: "suppliers", Page: Suppliers },
  { path: "cash", screen: "cash", Page: CashDrawer },
  { path: "savings", screen: "savings", Page: Savings },
  { path: "modules", screen: "modules", Page: Modules },
  { path: "subscription", screen: "subscription", Page: Subscription },
  { path: "referrals", screen: "referrals", Page: Referrals },
];


/**
 * Screens a person can reach before this install is activated, or before
 * they have an account at all: the link in a reset or verification email is
 * opened wherever the person happens to be, usually on a browser that is not
 * one of the shop's devices.
 */
const PUBLIC_PATHS = ["/signup", "/forgot-password", "/reset-password", "/verify-email", "/terms", "/privacy"];

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  // Signed in, but still holding a password or PIN someone else chose
  return user ? <CredentialGate>{children}</CredentialGate> : <Navigate to="/login" replace />;
};

/**
 * Stands in front of the whole app until this install is a licensed device.
 *
 * Nothing here enforces the limit — the API does that, and would refuse an
 * unactivated device even if this component were bypassed. What this does is
 * turn that refusal into something the shop can act on, instead of a screen
 * of failed requests.
 */
function DeviceGate({ children }) {
  const [activated, setActivated] = useState(() => Boolean(deviceToken.get()));

  useEffect(() => {
    // Raised by the API client when the server rejects this device, which
    // also covers a device the owner revoked from somewhere else.
    const onRejected = () => {
      // This shop's token only: the user's other shops still know this device
      const shopId = shopSession.currentId();
      if (shopId) shopSession.forget(shopId);
      deviceToken.clear();
      setActivated(false);
    };

    window.addEventListener("device-not-activated", onRejected);
    return () => window.removeEventListener("device-not-activated", onRejected);
  }, []);

  if (!activated && !PUBLIC_PATHS.includes(window.location.pathname)) {
    return <ActivateDevice onActivated={() => setActivated(true)} />;
  }

  return children;
}

/**
 * Takes over the whole app — register and back-office alike — the moment any
 * request comes back SHOP_SUSPENDED. Once shown it stays up until sign-out:
 * there's no API call that would tell us the shop is active again short of
 * signing back in.
 */
function SuspendedGate({ children }) {
  const { logout } = useAuth();
  const [suspension, setSuspension] = useState(null);

  useEffect(() => {
    const onSuspended = (event) => setSuspension(event.detail);
    window.addEventListener("shop-suspended", onSuspended);
    return () => window.removeEventListener("shop-suspended", onSuspended);
  }, []);

  if (suspension) {
    return (
      <SuspendedScreen
        shopName={suspension.shopName}
        message={suspension.message}
        onSignOut={() => {
          logout();
          setSuspension(null);
        }}
      />
    );
  }

  return children;
}

/**
 * Signing in is the landing screen: the app opens on the login form, and a
 * signed-in owner or cashier lands on the dashboard they're authorized to
 * see. The register itself lives behind the same login, and additionally
 * behind the till PIN — "Register" in the sidebar sends a signed-in person
 * to "/", where PosRegister pops the PIN pad if nobody has unlocked it yet.
 *
 * The language provider wraps everything, activation included: a device is
 * set up by someone who may not read English.
 */
function App() {
  return (
    <I18nProvider>
      <StagingBanner />
      <PullToRefresh />
      <AuthProvider>
        <SuspendedGate>
          <DeviceGate>
            <BrowserRouter>
              <ErrorBoundary>
              <Routes>
                <Route
                  index
                  element={
                    <PrivateRoute>
                      <PosRegister />
                    </PrivateRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/terms" element={<LegalPage kind="terms" />} />
                <Route path="/privacy" element={<LegalPage kind="privacy" />} />
                <Route
                  path="/admin/*"
                  element={
                    <PrivateRoute>
                      <MembershipProvider>
                        <OnboardingProvider>
                          <Layout />
                        </OnboardingProvider>
                      </MembershipProvider>
                    </PrivateRoute>
                  }
                >
                  {ADMIN_SCREENS.map(({ path, screen, Page }) => (
                    <Route
                      key={path || "index"}
                      index={!path}
                      path={path}
                      element={
                        <RequirePermission screen={screen}>
                          <Page />
                        </RequirePermission>
                      }
                    />
                  ))}
                  {/* Its stock-value report moved onto the dashboard */}
                  <Route
                    path="analytics"
                    element={<Navigate to="/admin" replace />}
                  />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </ErrorBoundary>
            </BrowserRouter>
          </DeviceGate>
        </SuspendedGate>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
